import { isPlatformBrowser } from '@angular/common'
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core'
import { Router } from '@angular/router'
import { TranslocoService } from '@jsverse/transloco'

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private readonly router = inject(Router)
    private readonly translocoService = inject(TranslocoService)
    private readonly platformId = inject(PLATFORM_ID)

    // Signal to hold the current language, accessible by components
    readonly currentLanguage = signal<string>(this.translocoService.getDefaultLang())
    readonly languagePrefix = computed(() => (this.currentLanguage() === 'en' ? '' : `/${this.currentLanguage()}`))

    setLanguageFromUrl(url: string): string {
        const lang = url.startsWith('/pl') ? 'pl' : 'en'
        this.setActiveLanguageInternal(lang)
        return lang
    }

    private setActiveLanguageInternal(lang: string): void {
        this.translocoService.setActiveLang(lang)
        this.currentLanguage.set(lang) // Update the signal
    }

    switchLanguage(targetLang: 'en' | 'pl'): void {
        if (!isPlatformBrowser(this.platformId)) {
            return // Don't attempt navigation/localStorage on server
        }

        const currentUrl = this.router.url
        const currentLang = this.currentLanguage() // Get from signal
        let newUrl: string

        if (targetLang === currentLang) {
            return // Already in the target language
        }

        newUrl = this.getCorrectUrlForLang(currentUrl, targetLang)

        // Navigate. The listener will handle setting the active lang after navigation.
        this.router.navigateByUrl(newUrl, { skipLocationChange: false })
    }

    /**
     * Individual blog posts exist in English only -- there is one markdown file
     * per slug and /pl/blog/[slug] merely re-exports the English component, so
     * those routes are not prerendered and would 404. Switching to Polish from
     * a post therefore falls back to the Polish blog index, which is a real
     * translated page. The blog index itself (/blog) is excluded: it has a
     * genuine Polish counterpart.
     */
    private readonly enOnlyPathToPlFallback: { matches: (path: string) => boolean; fallback: string }[] = [
        {
            matches: path => path.startsWith('/blog/'),
            fallback: '/pl/blog'
        }
    ]

    public getCorrectUrlForLang(currentUrl: string, targetLang: string): string {
        const isCurrentlyPl = currentUrl.startsWith('/pl')
        if (targetLang === 'pl' && !isCurrentlyPl) {
            // Strip query/fragment before matching so /blog/slug#section is still recognised.
            const path = currentUrl.split(/[?#]/)[0]
            const enOnly = this.enOnlyPathToPlFallback.find(entry => entry.matches(path))
            if (enOnly) {
                return enOnly.fallback
            }
            return currentUrl === '/' ? '/pl' : `/pl${currentUrl}`
        } else if (targetLang === 'en' && isCurrentlyPl) {
            const newUrl = currentUrl === '/pl' ? '/' : currentUrl.substring(3)
            return newUrl === '' ? '/' : newUrl
        }
        return currentUrl // URL already matches target language
    }
}
